import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { chain, intersectionBy, some } from 'lodash';
import { Subject } from 'rxjs';

import { App, FieldIdentifier, MapSiteDataPreview, Project, SelectableField } from '../../models';
import { FieldType, SiteFieldsNumericIds } from '../../shared/enums';
import { MapDataService } from '../data-services';
import { DragAndDropFieldSelectionUtilityService } from '../utility-services';
import { MapSiteDataPreviewFieldSelectionServiceModel } from './models';

@Injectable({
    providedIn: 'root'
})

export class MapSiteDataPreviewFieldSelectionLogicService {
    private _mapSiteDataPreviewLoaded = new Subject<boolean>();
    mapSiteDataPreviewLoaded$ = this._mapSiteDataPreviewLoaded.asObservable();

    model: MapSiteDataPreviewFieldSelectionServiceModel;

    get mapFiltersForm(): UntypedFormGroup {
        return this.model.mapSiteDataPreviewForm;
    }

    get availableFields(): SelectableField[] {
        return this.model.availableFields;
    }

    set availableFields(inAvailableFields: SelectableField[]) {
        this.model.availableFields = inAvailableFields;
    }

    get allFields(): SelectableField[] {
        return this.model.allFields;
    }

    get selectedFields(): SelectableField[] {
        return this.model.selectedFields;
    }

    set selectedFields(inSelectFields: SelectableField[]) {
        if (inSelectFields?.length) {
            inSelectFields.splice(this.model.locationFieldIndex, 0, ...this.setDefaultSelectedFields());
            this.model.selectedFields = [...inSelectFields];
            this.model.mapSiteDataPreview.selectedFields = this.model.selectedFields.map((field: SelectableField) => {
                return field.identifier;
            });
            if (this.allFields?.length) {
                this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateSelectableFields(this.selectedFields, this.allFields);
                this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableSelectableFields(this.selectedFields, this.availableFields);
            }
            else {
                this.model.availableFields = [];
            }
        }
        else {
            this.model.availableFields = [...this.allFields];
            this.model.selectedFields = [... this.setDefaultSelectedFields()];
            this.model.mapSiteDataPreview.selectedFields = this.model.selectedFields.map((field: SelectableField) => {
                return field.identifier;
            });
        }

        this.model.mapSiteDataPreviewForm.patchValue({
            selectedFields: inSelectFields,
        });
    }

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _dragAndDropFieldSelectionUtilityService: DragAndDropFieldSelectionUtilityService,
        private _mapDataService: MapDataService,
        private _snackBar: MatSnackBar,
    ) { }

    initModel(): void {
        this.model = new MapSiteDataPreviewFieldSelectionServiceModel();
        this.model.projectId = null;
        this.model.selectedFields = Array<SelectableField>();
        this.model.availableFields = Array<SelectableField>();
        this.model.allFields = Array<SelectableField>();
        this.model.mapSiteDataPreviewForm = this._formBuilder.group({
            id: new UntypedFormControl(),
            projectId: new UntypedFormControl(),
            selectedFields: ([])
        });
        this.model.mapSiteDataPreview = new MapSiteDataPreview();
    }

    loadModel(incomingPreview: MapSiteDataPreview): void {
        this.model.mapSiteDataPreview = incomingPreview;
        this.model.locationFieldIndex = incomingPreview.selectedFields.findIndex(field => field.fieldId == SiteFieldsNumericIds.Coordinates);
        this.model.projectId = incomingPreview.projectId;
        this.selectedFields = incomingPreview.selectedFields.map((field: FieldIdentifier) => {
            return this.allFields.find((f: SelectableField) => f.identifier.fieldId === field.fieldId && field.fieldId !== SiteFieldsNumericIds.Coordinates);
        }).filter((field: SelectableField) => field !== undefined);
    }

    loadForm(): void {
        this.model.mapSiteDataPreviewForm.patchValue({
            projectId: this.model.projectId,
            selectedFields: this.model.selectedFields
        });
    }

    setDefaultFields(allIncomingFields: SelectableField[], siteAlias: string): SelectableField[] {
        if (!allIncomingFields) {
            allIncomingFields = new Array<SelectableField>();
        }

        const cityField: SelectableField = { identifier: { fieldId: SiteFieldsNumericIds.City }, name: `${siteAlias} City`, breadcrumb: "", fieldTypeId: '1', enabled: true, hash: '', icon: '', uniqueReplacementValue: '', uniqueReplacementHtmlValue: '' };
        const stateField: SelectableField = { identifier: { fieldId: SiteFieldsNumericIds.State }, name: `${siteAlias} State`, breadcrumb: "", fieldTypeId: '1', enabled: true, hash: '', icon: '', uniqueReplacementValue: '', uniqueReplacementHtmlValue: '' };

        allIncomingFields.sort((a, b) => (a.name > b.name) ? 1 : -1);

        allIncomingFields = allIncomingFields.filter((field: SelectableField) => {
            return field.identifier.fieldId !== SiteFieldsNumericIds.City && field.identifier.fieldId !== SiteFieldsNumericIds.State;
        });

        allIncomingFields.unshift(stateField);
        allIncomingFields.unshift(cityField);

        return allIncomingFields;
    }

    setDefaultSelectedFields(): SelectableField[] {
        const locationField: SelectableField = {
            fieldTypeId: FieldType.TextBox,
            enabled: true,
            identifier: {
                fieldId: SiteFieldsNumericIds.Coordinates,
                gridColumnId: null,
            },
            name: 'Coordinates',
            hash: 'coordinates',
            uniqueReplacementHtmlValue: `<span class='khts-html-replaceable-field' id='coordinates'>Coordinates</span>`,
            uniqueReplacementValue: '{coordinates}',
            icon: 'location_on',
            breadcrumb: ``,
        };
        return [locationField];
    }

    dropAvailable(event: CdkDragDrop<SelectableField[]>, selectedFieldsList: CdkDropList<SelectableField[]>): void {
        this._dragAndDropFieldSelectionUtilityService.handleDragDropEvent(event);

        this.handleFieldsSelection(selectedFieldsList);
    }

    dropSelected(event: CdkDragDrop<SelectableField[]>, selectedFieldsList: CdkDropList<SelectableField[]>): void {
        this._dragAndDropFieldSelectionUtilityService.handleDragDropEvent(event);

        this.handleFieldsSelection(selectedFieldsList);
    }

    handleFieldsSelection(selectedFieldsList: CdkDropList<SelectableField[]>): void {
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateSelectableFields(this.model.selectedFields, this.allFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAvailableSelectableFields(this.model.selectedFields, this.model.availableFields);

        this.model.mapSiteDataPreviewForm.patchValue({
            selectedFields: selectedFieldsList.data,
        });

        this.model.mapSiteDataPreview.selectedFields = this.model.selectedFields.map((field: SelectableField) => {
            return field.identifier;
        });
    }

    fieldFilterChange(filterString: string): void {
        this.resetAvailableFields();
        const searchText = filterString.toLowerCase();
        this.model.availableFields = this.model.availableFields.filter((field: SelectableField) => {
            return field.name.toLowerCase().includes(searchText)
                || (field.breadcrumb ?? '').toLowerCase().includes(searchText);
        });
    }

    resetAvailableFields(): void {
        if (this.model.selectedFields?.length) {
            if (this.allFields?.length) {
                const allFieldsByIsSelected = chain(this.allFields)
                    .groupBy((field: SelectableField) => some(this.model.selectedFields, (selectedField: SelectableField) => selectedField.name === field.name && selectedField.identifier.fieldId === field.identifier.fieldId))
                    .value();
                this.model.availableFields = allFieldsByIsSelected["false"] || [];
            }
            else {
                this.model.availableFields = [];
            }
        }
        else {
            this.model.availableFields = chain(this.allFields).orderBy('name').value();
        }
    }

    unselectAll(): void {
        this.selectedFields = [];
        this.model.availableFields = [...this.allFields];
    }

    setAllFields(allIncomingFields: SelectableField[], siteAlias: string): void {
        allIncomingFields = this.setDefaultFields(allIncomingFields, siteAlias);

        this.selectedFields = intersectionBy(
            this.selectedFields,
            this.model.allFields,
            'identifier.fieldId'
        );

        this.model.allFields = this._dragAndDropFieldSelectionUtilityService.removeGridFieldsFromAllSelectableFields(allIncomingFields);
        this.model.availableFields = this._dragAndDropFieldSelectionUtilityService.removeDuplicateSelectableFields(this.selectedFields, this.model.allFields);
    }

    getMapSiteDataPreviewByProjectId(app: App, project: Project): void {
        if (app.id == this.model.previousApp?.id && project.id == this.model.previousProject?.id) {
            return;
        }

        this.model.previousApp = app;
        this.model.previousProject = project;
        this._mapDataService.getMapSiteDataPreviewByProjectId(app, project)
            .subscribe({
                next: (incomingPreview: MapSiteDataPreview) => {
                    if (incomingPreview?.id) {
                        this.loadModel(incomingPreview);
                        this.loadForm();
                        this._snackBar.open(`Map ${app.aliases.site.singular} Data Preview loaded`, `Close`, { duration: 5000 });
                    }
                    else {
                        this.initModel();
                        this.model.projectId = project.id;
                        this.model.selectedFields = new Array<SelectableField>();
                    }
                },
                error: () => {
                    this._snackBar.open(`Error loading ${app.aliases.site.singular} Site Data Preview`, 'Close', { duration: 5000 });
                }
            }).add(() => {
                this._mapSiteDataPreviewLoaded.next(true);
            });
    }

    saveMapSiteDataPreview(app: App, project: Project): void {
        if (this.model.mapSiteDataPreview.id) {
            this.updateMapSiteDataPreview(app, project);
        }
        else {
            this.createMapSiteDataPreview(app, project);
        }
    }

    createMapSiteDataPreview(app: App, project: Project): void {
        this._mapDataService.createMapSiteDataPreview(app, project, this.model.mapSiteDataPreview)
            .subscribe({
                next: (createdPreview: MapSiteDataPreview) => {
                    this.loadModel(createdPreview);
                    this.loadForm();
                    this._snackBar.open(`Map ${app.aliases.site.singular} Data Preview created successfully.`, 'Close', { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`Error creating Map ${app.aliases.site.singular} Data Preview`, 'Close', { duration: 5000 });
                }
            });
    }

    updateMapSiteDataPreview(app: App, project: Project): void {
        this._mapDataService.updateSiteDataPreview(app, project, this.model.mapSiteDataPreview)
            .subscribe({
                next: (updatedPreview: MapSiteDataPreview) => {
                    this.loadModel(updatedPreview);
                    this.loadForm();
                    this._snackBar.open(`Map ${app.aliases.site.singular} Data Preview updated successfully.`, 'Close', { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`Error updating Map ${app.aliases.site.singular} Data Preview`, 'Close', { duration: 5000 });
                }
            });
    }
}
